import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Link,
  Paper,
  Divider,
  Checkbox,
  FormControlLabel,
  styled,
  FormControl,
  Avatar,
  useTheme,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from "react-router-dom";
import {
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  ArrowForward,
} from "@mui/icons-material";
import CallIcon from "@mui/icons-material/Call";
import useAxios from "../useAxios";
import { motion } from "framer-motion";
import { useDispatch } from "react-redux";
import { loginSuccess } from "../redux/slices/authSlice";
import ErrorDialog from "./ErrorDialog";

const AuthCard = styled(Paper)(({ theme }) => ({
  maxWidth: 460,
  width: "100%",
  padding: theme.spacing(4),
  borderRadius: 20,
  backgroundColor: "rgba(255,255,255,0.8)",
  backdropFilter: "blur(12px)",
  boxShadow: "0 10px 40px rgba(183,28,28,0.25)",
  // [theme.breakpoints.down('sm')]: {
  //   padding: theme.spacing(3),
  //   borderRadius: 16,
  //   maxWidth: '80%',
  //   margin: '0 auto'
  // },
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(3),
    maxWidth: "90%",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2.5),
    borderRadius: 16,
  },
  [theme.breakpoints.down("xs")]: {
    padding: theme.spacing(2),
    borderRadius: 12,
  },
}));

export default function LoginForm({ onLogin, onSwitchToRegister }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isXs = useMediaQuery(theme.breakpoints.down("xs"));
  const isSm = useMediaQuery(theme.breakpoints.down("sm"));
  const isMd = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const axiosData = useAxios();
  const [step, setStep] = useState("mobile"); // "mobile" or "otp"
  const [otpfinal, setOtpfinal] = useState("");
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [timeLeft, setTimeLeft] = useState(0);
  const [loading, setLoading] = useState(false);
  const [credentials, setCredentials] = useState({
    mobile_number: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const isFormValid = () => {
    const isMobileValid =
      credentials.mobile_number.trim() !== "" &&
      /^\+?[0-9]{8,15}$/.test(credentials.mobile_number.trim()); // + optional, 8�15 digits

    //return isMobileValid && credentials.password.trim() !== "" && !loading;
    return isMobileValid && !loading;
  };
  const showError = (message) => {
    setErrorMessage(message);
    setErrorOpen(true);
  };

  const handleCloseError = () => {
    setErrorOpen(false);
  };
  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      document.getElementById(`otp-${index - 1}`).focus();
    }
  };
  const handleChange = (e) => {
    setCredentials({ ...credentials, [e.target.name]: e.target.value });
  };
  const handleChangeOTP = (value, index) => {
    if (/^[0-9]?$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      setOtpfinal(newOtp.join(""));
      // move to next field automatically
      if (value && index < 3) {
        document.getElementById(`otp-${index + 1}`).focus();
      }
    }
  };
  // Timer countdown
  useEffect(() => {
    if (step === "otp" && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft((t) => t - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [timeLeft, step]);
  // Step 1: Send OTP
  const handleSendOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axiosData.post("createOtp", {
        mobile_number: parseInt(credentials.mobile_number),
      });
      setStep("otp");
      setTimeLeft(60); // 1 min
    } catch (err) {
      showError(err.response?.data?.message || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify OTP
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axiosData.post("verify-otp/user", {
        mobile_number: parseInt(credentials.mobile_number),
        otp: parseInt(otpfinal),
      });
      if (res.data.success) {
        const { token, user } = res.data;
        dispatch(loginSuccess({ token, ...user }));
        onLogin(credentials);
        navigate(
          user.user_type === "admin" ? "/admin-dashboard" : "/user-dashboard"
        );
      } else {
        showError(res.data.message || "Invalid OTP");
      }
    } catch (err) {
      showError(err.response?.data?.message || "OTP verification failed");
    } finally {
      setLoading(false);
    }
  };
   const forgetLinkSend = async (mobile) => {
    try {
      // Simulate API call for password reset
      const response = await axiosData.put(`getUser/forgetpass`, {
        mobile_number: parseInt(mobile),
      });
      if (response) {
      }
    } catch (err) {
      console.error(err)
    } finally {
    }
  };
  const handleSubmit = async (e) => {
    setLoading(true);
    e.preventDefault();
    try {
      const response = await axiosData.post("login", credentials);
      if (response.data) {
        const { token } = response.data;
        const { user_type, email } = response.data.user;
        // Save in redux
        dispatch(loginSuccess({ token, user_type, email }));
        onLogin(credentials);
        setLoading(false);
        // Navigate
        if (user_type === "admin") {
          navigate("/admin-dashboard", { replace: true });
        } else {
          navigate("/user-dashboard", { replace: true });
        }
      }
    } catch (err) {
      setLoading(false);
      console.error("Login error", err);
      showError(err.response?.data || "Login failed. Please try again.");
    }
  };
  const logoWidth = isXs ? "85%" : isSm ? "75%" : "60%";
  const inputSize = isXs ? "small" : "medium";
  const buttonSize = isXs ? "small" : isSm ? "medium" : "large";
  const typographyVariant = isXs ? "body2" : "body1";
  const spacing = isXs ? 2 : isSm ? 2.5 : 3;
  return (
    <motion.div
      initial={{ x: -50, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      transition={{ duration: 0.6 }}
      style={{
        width: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        padding: isXs
          ? theme.spacing(1)
          : isSm
          ? theme.spacing(1)
          : theme.spacing(0),
      }}
    >
      <AuthCard>
        <Box textAlign="center" mb={spacing}>
          <Avatar
            src="/Logo2.png"
            variant="square"
            sx={{
              width: logoWidth,
              mx: "auto",
              height: "auto",
              maxWidth: 300,
              mb: 1,
            }}
          />
          <Typography
            color="text.secondary"
            variant={typographyVariant}
            sx={{ mt: 0.5 }}
          >
            Sign in to your account
          </Typography>
        </Box>

        <form onSubmit={handleSubmit}>
          <FormControl fullWidth margin="normal">
            <TextField
              name="mobile_number"
              label="Mobile Number"
              type="tel"
              variant="outlined"
              placeholder="Enter your mobile number"
              required
              value={credentials.mobile_number}
              onChange={(e) => {
                // Only allow digits
                const value = e.target.value.replace(/\D/g, "");
                setCredentials({ ...credentials, mobile_number: value });
              }}
              size={inputSize}
              inputProps={{
                pattern: "^[0-9]{8,15}$", // only digits, 8�15
                maxLength: 15, // max 15 digits
                inputMode: "numeric", // mobile keyboard shows numbers only
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CallIcon color="action" fontSize={inputSize} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 12,
                },
                "& input:-webkit-autofill": {
                  WebkitBoxShadow: "0 0 0 1000px transparent inset !important",
                  WebkitTextFillColor: "#000 !important",
                  transition: "background-color 5000s ease-in-out 0s",
                  caretColor: "#000",
                },
              }}
            />
          </FormControl>

          <FormControl fullWidth margin="normal">
            <TextField
              name="password"
              label="Password"
              type={showPassword ? "text" : "password"}
              variant="outlined"
              placeholder="Enter your password"
              required
              value={credentials.password}
              onChange={handleChange}
              size={inputSize}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="action" fontSize={inputSize} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size={inputSize}
                    >
                      {showPassword ? (
                        <VisibilityOff fontSize={inputSize} />
                      ) : (
                        <Visibility fontSize={inputSize} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 12,
                },
                "& input:-webkit-autofill": {
                  WebkitBoxShadow: "0 0 0 1000px transparent inset !important",
                  WebkitTextFillColor: "#000 !important",
                  transition: "background-color 5000s ease-in-out 0s",
                  caretColor: "#000",
                },
              }}
            />
          </FormControl>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Typography
              color="error"
              sx={{
                fontSize: 13,
                cursor: "pointer",
                //ml: { xs: 18, sm: 42, md: 42 }, // 43px = 5.375rem (assuming 8px base)
                textAlign: "center",
              }}
            >
              <Link
                component="button"
                variant="body2"
                underline="always"
                color="primary"
                fontWeight={500}
                onClick={(e) => {
                  // if (credentials.mobile_number === "") {
                  //   showError("Please enter your mobile number");
                  // } else {
                     e.preventDefault(); 
                    //forgetLinkSend(credentials.mobile_number)
                    navigate("/otp-verify", {
                      // state: { mobile_number: credentials.mobile_number },
                    });
                  // }
                }}
                sx={{
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  width: { xs: "100%", sm: "auto" },
                  display: "block",
                }}
              >
                Forgot Password?
              </Link>
            </Typography>
          </div>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            size={buttonSize}
            type="submit"
            disabled={!isFormValid()}
            endIcon={loading ? null : <ArrowForward fontSize={inputSize} />}
            sx={{
              mt: spacing,
              py: isXs ? 0.8 : isSm ? 1 : 0.8,
              borderRadius: 12,
              fontSize: isXs ? "0.8rem" : isSm ? "0.9rem" : "1rem",
              fontWeight: 600,
              textTransform: "none",
              backgroundColor: "#D32F2F",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#B71C1C",
                transform: "translateY(-2px)",
                boxShadow: "0 6px 20px rgba(0, 0, 0, 0.15)",
              },
              "&.Mui-disabled": {
                backgroundColor: "rgba(211, 47, 47, 0.5)",
              },
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Sign In"
            )}
          </Button>

          <Divider sx={{ my: spacing }}>
            <Typography
              variant={isXs ? "caption" : "body2"}
              color="text.secondary"
            >
              OR
            </Typography>
          </Divider>

          <Box textAlign="center" mt={spacing - 1}>
            <Typography variant={isXs ? "caption" : "body2"}>
              Don't have an account?{" "}
              <Link
                component="button"
                variant={isXs ? "caption" : "body2"}
                underline="always"
                color="primary"
                fontWeight={500}
                onClick={onSwitchToRegister}
                sx={{
                  fontSize: isXs ? "0.75rem" : "inherit",
                }}
              >
                Create account
              </Link>
            </Typography>
          </Box>
        </form>
      </AuthCard>

      <ErrorDialog
        open={errorOpen}
        onClose={handleCloseError}
        title="Login Error"
        message={errorMessage}
      />
    </motion.div>
    // <motion.div
    //   initial={{ x: -50, opacity: 0 }}
    //   animate={{ x: 0, opacity: 1 }}
    //   transition={{ duration: 0.6 }}
    // >
    //   <AuthCard>
    //     <Box textAlign="center" mb={isMobile ? 2 : 3}>
    //       <Avatar
    //         src="/Logo2.png"
    //         variant="square"
    //         sx={{
    //           width: isMobile ? "80%" : "60%",
    //           mx: "auto",
    //           height: "auto",
    //           maxWidth: 300
    //         }}
    //       />
    //       <Typography
    //         color="text.secondary"
    //         variant={isMobile ? "body2" : "body1"}
    //         sx={{ mt: 1 }}
    //       >
    //         Sign in to your account
    //       </Typography>
    //     </Box>

    //     <form onSubmit={handleSubmit}>
    //       <FormControl fullWidth margin="normal">
    //         <TextField
    //           name="email"
    //           label="Email Address"
    //           type="email"
    //           variant="outlined"
    //           placeholder="Enter your email"
    //           required
    //           value={credentials.email}
    //           onChange={handleChange}
    //           size={isMobile ? "small" : "medium"}
    //           InputProps={{
    //             startAdornment: (
    //               <InputAdornment position="start">
    //                 <EmailIcon color="action" fontSize={isMobile ? "small" : "medium"} />
    //               </InputAdornment>
    //             ),
    //           }}
    //           sx={{
    //             '& .MuiOutlinedInput-root': {
    //               borderRadius: 12,
    //             },
    //           }}
    //         />
    //       </FormControl>

    //       <FormControl fullWidth margin="normal">
    //         <TextField
    //           name="password"
    //           label="Password"
    //           type={showPassword ? 'text' : 'password'}
    //           variant="outlined"
    //           placeholder="Enter your password"
    //           required
    //           value={credentials.password}
    //           onChange={handleChange}
    //           size={isMobile ? "small" : "medium"}
    //           InputProps={{
    //             startAdornment: (
    //               <InputAdornment position="start">
    //                 <LockIcon color="action" fontSize={isMobile ? "small" : "medium"} />
    //               </InputAdornment>
    //             ),
    //             endAdornment: (
    //               <InputAdornment position="end">
    //                 <IconButton
    //                   onClick={() => setShowPassword(!showPassword)}
    //                   edge="end"
    //                   size={isMobile ? "small" : "medium"}
    //                 >
    //                   {showPassword ? <VisibilityOff fontSize={isMobile ? "small" : "medium"} /> : <Visibility fontSize={isMobile ? "small" : "medium"} />}
    //                 </IconButton>
    //               </InputAdornment>
    //             ),
    //           }}
    //           sx={{
    //             '& .MuiOutlinedInput-root': {
    //               borderRadius: 12,
    //             },
    //           }}
    //         />
    //       </FormControl>

    //       <Button
    //         fullWidth
    //         variant="contained"
    //         color="primary"
    //         size={isMobile ? "medium" : "large"}
    //         type="submit"
    //          disabled={!isFormValid()}
    //         endIcon={<ArrowForward fontSize={isMobile ? "small" : "medium"} />}
    //         sx={{
    //           mt: 3,
    //           py: isMobile ? 1 : 1.5,
    //           borderRadius: 12,
    //           fontSize: isMobile ? '0.875rem' : '1rem',
    //           fontWeight: 600,
    //           textTransform: 'none',
    //           backgroundColor: '#D32F2F',
    //           boxShadow: 'none',
    //           '&:hover': {
    //             backgroundColor: '#B71C1C',
    //             transform: 'translateY(-2px)',
    //             boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
    //           },
    //         }}
    //       >
    //         Sign In
    //       </Button>

    //       <Divider sx={{ my: isMobile ? 2 : 3 }}>
    //         <Typography variant={isMobile ? "caption" : "body2"} color="text.secondary">
    //           OR
    //         </Typography>
    //       </Divider>

    //       <Box textAlign="center" mt={isMobile ? 1 : 2}>
    //         <Typography variant={isMobile ? "caption" : "body2"}>
    //           Don't have an account?{' '}
    //           <Link
    //             component="button"
    //             variant={isMobile ? "caption" : "body2"}
    //             underline="always"
    //             color="primary"
    //             fontWeight={500}
    //             onClick={onSwitchToRegister}
    //           >
    //             Create account
    //           </Link>
    //         </Typography>
    //       </Box>
    //     </form>
    //   </AuthCard>

    //   <ErrorDialog
    //     open={errorOpen}
    //     onClose={handleCloseError}
    //     title="Operation Failed"
    //     message={errorMessage}
    //   />
    // </motion.div>
  );
  // return (
  //   <motion.div
  //     initial={{ x: -50, opacity: 0 }}
  //     animate={{ x: 0, opacity: 1 }}
  //     transition={{ duration: 0.6 }}
  //     style={{
  //       width: "100%",
  //       display: "flex",
  //       justifyContent: "center",
  //       alignItems: "center",
  //       minHeight: "100vh",
  //       padding: isXs
  //         ? theme.spacing(1)
  //         : isSm
  //         ? theme.spacing(1)
  //         : theme.spacing(0),
  //     }}
  //   >
  //     <AuthCard>
  //       <Box textAlign="center" mb={spacing}>
  //         <Avatar
  //           src="/Logo2.png"
  //           variant="square"
  //           sx={{
  //             width: logoWidth,
  //             mx: "auto",
  //             height: "auto",
  //             maxWidth: 300,
  //             mb: 1,
  //           }}
  //         />
  //         <Typography color="text.secondary">
  //           Sign in to your account
  //         </Typography>
  //       </Box>

  //       {step === "mobile" && (
  //         <form onSubmit={handleSendOtp}>
  //           <FormControl fullWidth margin="normal">
  //             <TextField
  //               name="mobile_number"
  //               label="Mobile Number"
  //               type="tel"
  //               placeholder="Enter your mobile number"
  //               required
  //               value={credentials.mobile_number}
  //               onChange={(e) =>
  //                 setCredentials({
  //                   ...credentials,
  //                   mobile_number: e.target.value.replace(/\D/g, ""),
  //                 })
  //               }
  //               inputProps={{
  //                 pattern: "^[0-9]{8,15}$",
  //                 maxLength: 15,
  //                 inputMode: "numeric",
  //               }}
  //               size={inputSize}
  //               margin="normal"
  //               InputProps={{
  //                 startAdornment: (
  //                   <InputAdornment position="start">
  //                     <CallIcon color="action" />
  //                   </InputAdornment>
  //                 ),
  //               }}
  //               sx={{
  //                 "& .MuiOutlinedInput-root": {
  //                   borderRadius: 12,
  //                 },
  //                 "& input:-webkit-autofill": {
  //                   WebkitBoxShadow:
  //                     "0 0 0 1000px transparent inset !important",
  //                   WebkitTextFillColor: "#000 !important",
  //                   transition: "background-color 5000s ease-in-out 0s",
  //                   caretColor: "#000",
  //                 },
  //               }}
  //             />
  //           </FormControl>
  //           <Button
  //             fullWidth
  //             variant="contained"
  //             size={buttonSize}
  //             type="submit"
  //             disabled={!isFormValid()}
  //             sx={{
  //               mt: spacing,
  //               py: isXs ? 0.8 : isSm ? 1 : 1.5,
  //               borderRadius: 12,
  //               fontSize: isXs ? "0.8rem" : isSm ? "0.9rem" : "1rem",
  //               fontWeight: 600,
  //               textTransform: "none",
  //               backgroundColor: "#D32F2F",
  //               boxShadow: "none",
  //               "&:hover": {
  //                 backgroundColor: "#B71C1C",
  //                 transform: "translateY(-2px)",
  //                 boxShadow: "0 6px 20px rgba(0, 0, 0, 0.15)",
  //               },
  //               "&.Mui-disabled": {
  //                 backgroundColor: "rgba(211, 47, 47, 0.5)",
  //               },
  //             }}
  //             endIcon={!loading && <ArrowForward fontSize={inputSize} />}
  //           >
  //             {loading ? (
  //               <CircularProgress size={24} color="inherit" />
  //             ) : (
  //               "Send OTP"
  //             )}
  //           </Button>
  //         </form>
  //       )}

  //       {step === "otp" && (
  //         <form onSubmit={handleVerifyOtp}>
  //           <Box display="flex" justifyContent="center" gap={2} mt={2}>
  //             {otp.map((digit, i) => (
  //               <TextField
  //                 key={i}
  //                 id={`otp-${i}`}
  //                 value={digit}
  //                 onChange={(e) => handleChangeOTP(e.target.value, i)}
  //                 onKeyDown={(e) => handleKeyDown(e, i)}
  //                 inputProps={{
  //                   maxLength: 1,
  //                   pattern: "[0-9]",
  //                   inputMode: "numeric",
  //                   style: {
  //                     textAlign: "center",
  //                     fontSize: "20px",
  //                     padding: "10px",
  //                     borderRadius: "100%",
  //                     width: "50px",
  //                     height: "50px",
  //                   },
  //                 }}
  //                 variant="outlined"
  //               />
  //             ))}
  //           </Box>
  //           {/* <FormControl fullWidth margin="normal">
  //           <TextField
  //           name="otp"
  //             label="Enter your OTP"
  //             value={otp}
  //             type="tel"
  //             required
  //             variant="outlined"
  //             onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
  //             inputProps={{ maxLength: 4, pattern: "[0-9]{4,4}" ,inputMode: "numeric"}}
  //             size={inputSize}
  //             InputProps={{
  //               startAdornment: (
  //                 <InputAdornment position="start">
  //                   <CallIcon color="action" fontSize={inputSize} />
  //                 </InputAdornment>
  //               ),
  //             }}
  //             margin="normal"
  //             sx={{
  //               "& .MuiOutlinedInput-root": {
  //                 borderRadius: 12,
  //               },
  //               "& input:-webkit-autofill": {
  //                 WebkitBoxShadow: "0 0 0 1000px transparent inset !important",
  //                 WebkitTextFillColor: "#000 !important",
  //                 transition: "background-color 5000s ease-in-out 0s",
  //                 caretColor: "#000",
  //               },
  //             }}
  //           />
  //           </FormControl> */}
  //           <Button
  //             fullWidth
  //             variant="contained"
  //             size={buttonSize}
  //             color="primary"
  //             type="submit"
  //             disabled={otpfinal.length < 4 || loading}
  //             endIcon={!loading && <ArrowForward fontSize={inputSize} />}
  //             sx={{
  //               mt: spacing,
  //               py: isXs ? 0.8 : isSm ? 1 : 1.5,
  //               borderRadius: 12,
  //               fontSize: isXs ? "0.8rem" : isSm ? "0.9rem" : "1rem",
  //               fontWeight: 600,
  //               textTransform: "none",
  //               backgroundColor: "#D32F2F",
  //               boxShadow: "none",
  //               "&:hover": {
  //                 backgroundColor: "#B71C1C",
  //                 transform: "translateY(-2px)",
  //                 boxShadow: "0 6px 20px rgba(0, 0, 0, 0.15)",
  //               },
  //               "&.Mui-disabled": {
  //                 backgroundColor: "rgba(211, 47, 47, 0.5)",
  //               },
  //             }}
  //           >
  //             {loading ? (
  //               <CircularProgress size={24} color="inherit" />
  //             ) : (
  //               "Verify OTP"
  //             )}
  //           </Button>

  //           <Box textAlign="center" mt={2}>
  //             {timeLeft > 0 ? (
  //               <Typography variant="body2" color="#D32F2F">
  //                 OTP expires in {timeLeft}s
  //               </Typography>
  //             ) : (
  //               <Button variant="text" onClick={handleSendOtp}>
  //                 Resend OTP
  //               </Button>
  //             )}
  //           </Box>
  //         </form>
  //       )}

  //       <Divider sx={{ my: spacing }}>
  //         <Typography
  //           variant={isXs ? "caption" : "body2"}
  //           color="text.secondary"
  //         >
  //           OR
  //         </Typography>
  //       </Divider>

  //       <Box textAlign="center" mt={spacing - 1}>
  //         <Typography variant={isXs ? "caption" : "body2"}>
  //           Don't have an account?{" "}
  //           <Link
  //             component="button"
  //             variant={isXs ? "caption" : "body2"}
  //             underline="always"
  //             color="primary"
  //             fontWeight={500}
  //             onClick={onSwitchToRegister}
  //             sx={{
  //               fontSize: isXs ? "0.75rem" : "inherit",
  //             }}
  //           >
  //             Create account
  //           </Link>
  //         </Typography>
  //       </Box>
  //     </AuthCard>

  //     <ErrorDialog
  //       open={errorOpen}
  //       onClose={handleCloseError}
  //       title="Login Error"
  //       message={errorMessage}
  //     />
  //   </motion.div>
  // );
}
